import React, { useMemo, useState } from 'react';
import {
  Alert,
  Autocomplete,
  Box,
  Card,
  CardContent,
  CircularProgress,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  Tab,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  TextField,
  Typography,
} from '@mui/material';
import AccountBalanceWalletIcon from '@mui/icons-material/AccountBalanceWallet';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import LocalShippingIcon from '@mui/icons-material/LocalShipping';
import ShoppingCartIcon from '@mui/icons-material/ShoppingCart';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import OutboxIcon from '@mui/icons-material/Outbox';
import ProcurementContainer from '../../procurement/containers/ProcurementContainer';
import {
  useGetContractItemsByContractQuery,
  useGetProcurementContractsQuery,
  useGetVirtualWarehouseSummaryQuery,
  VirtualWarehouseTransfer,
} from '../../../redux/procurementContractsApi';
import { getCurrentDate } from '../../../utils/dateUtil';
import { formatCurrency } from '../../../utils/currencyUtil';

type MetricCardProps = {
  title: string;
  value: number;
  caption: string;
  accent: string;
  icon: React.ReactNode;
  quantity?: number;
  quantityLabel?: string;
  quantityUnit?: string;
  showQuantity?: boolean;
  currencyCode?: string;
};

const MetricCard: React.FC<MetricCardProps> = ({
  title,
  value,
  caption,
  accent,
  icon,
  quantity,
  quantityLabel = 'Quantity',
  quantityUnit,
  showQuantity = false,
  currencyCode = 'USD',
}) => (
  <Card
    sx={{
      borderRadius: 3,
      height: '100%',
      background: `linear-gradient(145deg, ${accent}22 0%, #ffffff 80%)`,
      border: `1px solid ${accent}55`,
      boxShadow: `0 10px 24px ${accent}22`,
    }}
  >
    <CardContent>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" spacing={2}>
        <Typography variant="subtitle2" sx={{ color: '#2f3e46', fontWeight: 700 }}>
          {title}
        </Typography>
        <Box sx={{ color: accent }}>{icon}</Box>
      </Stack>

      <Typography variant="h5" sx={{ fontWeight: 800, color: accent, mt: 1.5 }}>
        {formatCurrency(value, currencyCode)}
      </Typography>
      <Typography variant="body2" sx={{ color: '#415a77', mt: 0.5 }}>
        {caption}
      </Typography>

      {showQuantity ? (
        <>
          <Typography variant="h6" sx={{ fontWeight: 700, color: '#1b263b', mt: 1.25 }}>
            {quantity?.toLocaleString(undefined, { maximumFractionDigits: 2 }) ?? '0'}
          </Typography>
          <Typography variant="body2" sx={{ color: '#415a77' }}>
            {quantityUnit ? `${quantityLabel} (${quantityUnit})` : quantityLabel}
          </Typography>
        </>
      ) : null}
    </CardContent>
  </Card>
);

const formatDate = (value?: string): string => {
  if (!value) return '-';
  const parsed = new Date(value);
  return Number.isNaN(parsed.getTime()) ? '-' : parsed.toLocaleDateString();
};

const EmptyRow: React.FC<{ colSpan: number }> = ({ colSpan }) => (
  <TableRow>
    <TableCell colSpan={colSpan} align="center" sx={{ py: 3, color: '#999' }}>
      No records found
    </TableCell>
  </TableRow>
);

const TransferTable: React.FC<{ rows: VirtualWarehouseTransfer[]; numberLabel: string; currencyCode: string }> = ({
  rows,
  numberLabel,
  currencyCode,
}) => (
  <Table size="small">
    <TableHead>
      <TableRow>
        <TableCell>{numberLabel}</TableCell>
        <TableCell>PO Number</TableCell>
        <TableCell>Date</TableCell>
        <TableCell align="right">Contract Items</TableCell>
        <TableCell align="right">Quantity</TableCell>
        <TableCell align="right">Value</TableCell>
      </TableRow>
    </TableHead>
    <TableBody>
      {rows.map((row, index) => (
        <TableRow key={row.id ?? `${row.number}-${index}`} hover>
          <TableCell>{row.number || '-'}</TableCell>
          <TableCell>{row.purchaseOrderNumber || '-'}</TableCell>
          <TableCell>{formatDate(row.transferredDate)}</TableCell>
          <TableCell align="right">{row.itemCount}</TableCell>
          <TableCell align="right">{row.quantity.toLocaleString(undefined, { maximumFractionDigits: 2 })}</TableCell>
          <TableCell align="right">{formatCurrency(row.value, row.currency || currencyCode)}</TableCell>
        </TableRow>
      ))}
      {rows.length === 0 && <EmptyRow colSpan={6} />}
    </TableBody>
  </Table>
);

const VirtualWarehousePage: React.FC = () => {
  const [fromDate, setFromDate] = useState('2025-01-01');
  const [toDateValue, setToDateValue] = useState(getCurrentDate());
  const [selectedContractId, setSelectedContractId] = useState('');
  const [selectedContractItemId, setSelectedContractItemId] = useState('all');
  const [selectedCurrency, setSelectedCurrency] = useState('USD');
  const [activeTab, setActiveTab] = useState(0);

  const { data: contracts = [], isLoading: contractsLoading } = useGetProcurementContractsQuery();
  const { data: contractItems = [] } = useGetContractItemsByContractQuery(
    { contractId: selectedContractId, limit: 5000, skip: 0 },
    { skip: !selectedContractId },
  );

  const contractItemOptions = useMemo(
    () => [
      { id: 'all', label: 'All Contract Items' },
      ...contractItems.map((item) => ({
        id: String(item.id),
        label: `${item.itemCode} - ${item.itemDescription}`,
      })),
    ],
    [contractItems],
  );

  const selectedContract = contracts.find((contract) => String(contract.id) === selectedContractId);
  const isSingleItemSelected = selectedContractItemId !== 'all';
  const statusReady = Boolean(selectedContractId);
  const currencyCode = selectedCurrency;

  const { data: summary, isFetching, isError } = useGetVirtualWarehouseSummaryQuery(
    {
      contractId: selectedContractId,
      contractItemId: isSingleItemSelected ? selectedContractItemId : undefined,
      fromDate,
      toDate: toDateValue,
      currency: selectedCurrency,
    },
    { skip: !statusReady },
  );

  const unit = summary?.unit ?? '';

  return (
    <ProcurementContainer>
      <Stack spacing={3}>
        <Paper
          sx={{
            p: 3,
            borderRadius: 4,
            background: 'linear-gradient(135deg, #e0f2fe 0%, #f3e8ff 100%)',
            border: '1px solid #d0d7de',
            boxShadow: '0 12px 28px rgba(15, 23, 42, 0.12)',
          }}
        >
          <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} justifyContent="space-between" alignItems={{ xs: 'flex-start', md: 'center' }}>
            <Box>
              <Typography variant="h4" sx={{ fontWeight: 800, color: '#0f172a' }}>
                Virtual Warehouse
              </Typography>
              <Typography variant="body1" sx={{ color: '#334155', mt: 1 }}>
                Track contracted stock held by suppliers on our behalf, from order to delivery and issue.
              </Typography>
            </Box>
            <Typography sx={{ fontWeight: 700, color: '#0f172a' }}>
              · Ordered · Delivered · Issued
            </Typography>
          </Stack>
        </Paper>

        <Paper sx={{ p: 3, borderRadius: 3, backgroundColor: '#f8fafc', boxShadow: 3 }}>
          <Typography variant="h6" sx={{ fontWeight: 700, mb: 2, color: '#1e293b' }}>
            Filters
          </Typography>
          <Grid container spacing={2}>
            <Grid item xs={12} md={3}>
              <TextField
                label="From Date"
                type="date"
                value={fromDate}
                onChange={(event) => setFromDate(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                label="To Date"
                type="date"
                value={toDateValue}
                onChange={(event) => setToDateValue(event.target.value)}
                fullWidth
                InputLabelProps={{ shrink: true }}
              />
            </Grid>
            <Grid item xs={12} md={6}>
              <FormControl fullWidth disabled={contractsLoading}>
                <InputLabel>Procurement Contract</InputLabel>
                <Select
                  value={selectedContractId}
                  label="Procurement Contract"
                  onChange={(event) => {
                    setSelectedContractId(event.target.value);
                    setSelectedContractItemId('all');
                  }}
                >
                  {contracts.map((contract) => (
                    <MenuItem key={String(contract.id)} value={String(contract.id)}>
                      {contract.title} - {contract.supplier?.name}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={6}>
              <Autocomplete
                options={contractItemOptions}
                value={contractItemOptions.find((opt) => opt.id === selectedContractItemId) ?? contractItemOptions[0] ?? null}
                onChange={(_event, option) => setSelectedContractItemId(option?.id ?? 'all')}
                disabled={!selectedContractId}
                getOptionLabel={(option) => option.label}
                isOptionEqualToValue={(option, value) => option.id === value.id}
                renderInput={(params) => <TextField {...params} label="Contract Item" fullWidth />}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl fullWidth>
                <InputLabel>Currency</InputLabel>
                <Select value={selectedCurrency} label="Currency" onChange={(event) => setSelectedCurrency(event.target.value)}>
                  <MenuItem value="USD">USD</MenuItem>
                  <MenuItem value="EUR">EUR</MenuItem>
                  <MenuItem value="ZAR">ZAR</MenuItem>
                  <MenuItem value="GBP">GBP</MenuItem>
                  <MenuItem value="AUD">AUD</MenuItem>
                </Select>
              </FormControl>
            </Grid>
          </Grid>

          {!statusReady && (
            <Alert severity="info" sx={{ mt: 2 }}>
              Select a Procurement Contract to load the virtual warehouse view.
            </Alert>
          )}

          {statusReady && (
            <Typography variant="body2" sx={{ color: '#334155', fontWeight: 600, mt: 2 }}>
              Supplier: {selectedContract?.supplier?.name ?? '-'}
            </Typography>
          )}
        </Paper>

        {statusReady && isError && <Alert severity="error">Failed to load virtual warehouse data.</Alert>}

        {statusReady && isFetching && !summary && (
          <Box sx={{ display: 'flex', justifyContent: 'center' }}>
            <CircularProgress />
          </Box>
        )}

        {statusReady && summary && (
          <>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Contract Value"
                  value={summary.contractValue}
                  quantity={summary.contractQty}
                  quantityLabel="Contracted quantity"
                  quantityUnit={unit}
                  showQuantity={isSingleItemSelected}
                  caption="Initial value locked in the contract"
                  accent="#0f766e"
                  icon={<AccountBalanceWalletIcon />}
                  currencyCode={currencyCode}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Ordered"
                  value={summary.orderedValue}
                  quantity={summary.orderedQty}
                  quantityLabel="Ordered quantity"
                  quantityUnit={unit}
                  showQuantity={isSingleItemSelected}
                  caption="Total procured from this contract"
                  accent="#7c3aed"
                  icon={<ShoppingCartIcon />}
                  currencyCode={currencyCode}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Delivered"
                  value={summary.deliveredValue}
                  quantity={summary.deliveredQty}
                  quantityLabel="Delivered quantity"
                  quantityUnit={unit}
                  showQuantity={isSingleItemSelected}
                  caption="Received into our physical locations"
                  accent="#0d9488"
                  icon={<LocalShippingIcon />}
                  currencyCode={currencyCode}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Undelivered"
                  value={summary.undeliveredValue}
                  quantity={summary.undeliveredQty}
                  quantityLabel="Undelivered quantity"
                  quantityUnit={unit}
                  showQuantity={isSingleItemSelected}
                  caption="Ordered but not yet in our physical locations"
                  accent="#dc2626"
                  icon={<Inventory2Icon />}
                  currencyCode={currencyCode}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Contract Value Left"
                  value={summary.contractValueLeft}
                  quantity={summary.contractQtyLeft}
                  quantityLabel="Contract quantity left"
                  quantityUnit={unit}
                  showQuantity={isSingleItemSelected}
                  caption="Remaining value across the selected contract items"
                  accent="#f59e0b"
                  icon={<TrendingUpIcon />}
                  currencyCode={currencyCode}
                />
              </Grid>
              <Grid item xs={12} md={4}>
                <MetricCard
                  title="Items Issued"
                  value={summary.issuedValue}
                  quantity={summary.issuedQty}
                  quantityLabel="Issued quantity"
                  quantityUnit={unit}
                  showQuantity={isSingleItemSelected}
                  caption="Issued out of our physical locations"
                  accent="#6366f1"
                  icon={<OutboxIcon />}
                  currencyCode={currencyCode}
                />
              </Grid>
            </Grid>

            <Paper sx={{ borderRadius: 3, boxShadow: 3 }}>
              <Tabs
                value={activeTab}
                onChange={(_event, value) => setActiveTab(value)}
                variant="scrollable"
                sx={{ borderBottom: 1, borderColor: 'divider', px: 2 }}
              >
                <Tab label={`Purchase Orders (${summary.purchaseOrders.length})`} />
                <Tab label={`Payment Requisitions (${summary.paymentRequisitions.length})`} />
                <Tab label={`GRVs (${summary.grvs.length})`} />
                <Tab label={`GIVs (${summary.givs.length})`} />
              </Tabs>

              <TableContainer sx={{ p: 1 }}>
                {activeTab === 0 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>PO Number</TableCell>
                        <TableCell>Date</TableCell>
                        <TableCell>Supplier</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Payment</TableCell>
                        <TableCell align="right">Contract Items Ordered</TableCell>
                        <TableCell align="right">Delivered</TableCell>
                        <TableCell align="right">Undelivered</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.purchaseOrders.map((row) => (
                        <TableRow key={row.purchaseOrderNumber} hover>
                          <TableCell>{row.purchaseOrderNumber}</TableCell>
                          <TableCell>{formatDate(row.date)}</TableCell>
                          <TableCell>{row.supplier || '-'}</TableCell>
                          <TableCell>{row.status || '-'}</TableCell>
                          <TableCell>{row.paymentStatus || '-'}</TableCell>
                          <TableCell align="right">{formatCurrency(row.ordered, currencyCode)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.delivered, currencyCode)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.undelivered, currencyCode)}</TableCell>
                        </TableRow>
                      ))}
                      {summary.purchaseOrders.length === 0 && <EmptyRow colSpan={8} />}
                    </TableBody>
                  </Table>
                )}

                {activeTab === 1 && (
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Reference</TableCell>
                        <TableCell>PO Number</TableCell>
                        <TableCell>Type</TableCell>
                        <TableCell>Status</TableCell>
                        <TableCell>Created</TableCell>
                        <TableCell>Due</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {summary.paymentRequisitions.map((row) => (
                        <TableRow key={row.id} hover>
                          <TableCell>{row.referenceNumber || '-'}</TableCell>
                          <TableCell>{row.purchaseOrderNumber || '-'}</TableCell>
                          <TableCell>{row.paymentType || '-'}</TableCell>
                          <TableCell>{row.status || '-'}</TableCell>
                          <TableCell>{formatDate(row.dateCreated)}</TableCell>
                          <TableCell>{formatDate(row.dueDate)}</TableCell>
                          <TableCell align="right">{formatCurrency(row.amount, row.currency || currencyCode)}</TableCell>
                        </TableRow>
                      ))}
                      {summary.paymentRequisitions.length === 0 && <EmptyRow colSpan={7} />}
                    </TableBody>
                  </Table>
                )}

                {activeTab === 2 && <TransferTable rows={summary.grvs} numberLabel="GRV Number" currencyCode={currencyCode} />}
                {activeTab === 3 && <TransferTable rows={summary.givs} numberLabel="GIV Number" currencyCode={currencyCode} />}
              </TableContainer>
            </Paper>
          </>
        )}
      </Stack>
    </ProcurementContainer>
  );
};

export default VirtualWarehousePage;
